import React, { useState, useEffect, useRef } from 'react';
import KoobInputSelect from './KoobInputSelect';

export interface LocaleOption {
  value: string;
  label: string;
  alpha2: string;
}

export interface KoobLocaleSwitcherProps {
  queryLocale?: LocaleOption | string | null;
  updateQueryLocale?: (locale: LocaleOption) => void;
  onChange?: (locale: LocaleOption) => void;
  getLanguages?: () => Promise<any>;
  locales?: LocaleOption[];
  disabled?: boolean;
  'data-testid'?: string;
}

const defaultLocales: LocaleOption[] = [
  { value: 'en', label: 'EN', alpha2: 'gb' },
  { value: 'fr', label: 'FR', alpha2: 'fr' },
];

export const KoobLocaleSwitcher: React.FC<KoobLocaleSwitcherProps> = ({
  queryLocale,
  updateQueryLocale,
  onChange,
  getLanguages,
  locales: localesProp,
  disabled = false,
  'data-testid': dataCy = 'locale-switcher',
}) => {
  const [languages, setLanguages] = useState<LocaleOption[]>(localesProp || []);
  const [loading, setLoading] = useState<boolean>(
    !localesProp && Boolean(getLanguages),
  );
  const hasFetched = useRef<boolean>(false);

  useEffect(() => {
    if (localesProp) {
      setLanguages(localesProp);
      return;
    }

    if (getLanguages && !hasFetched.current) {
      hasFetched.current = true;
      setLoading(true);
      getLanguages()
        .then((res: any) => {
          const rawList = res?.data || res || [];
          const mapped: LocaleOption[] = rawList.map((item: any) => {
            if (item.value && item.alpha2) {
              return {
                value: String(item.value),
                label: String(item.label || item.value).toUpperCase(),
                alpha2: String(item.alpha2).toLowerCase(),
              };
            }
            const name = String(item.name || item.value || '');
            const description = String(item.description || item.alpha2 || name);
            return {
              value: name,
              label: name.toUpperCase(),
              alpha2: description.toLowerCase(),
            };
          });
          setLanguages(mapped.length > 0 ? mapped : defaultLocales);
        })
        .catch(() => {
          setLanguages(defaultLocales);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [getLanguages, localesProp]);

  const selectOptions = languages.length > 0 ? languages : defaultLocales;

  const currentValue =
    typeof queryLocale === 'object' && queryLocale !== null
      ? queryLocale.value
      : queryLocale;

  const optionSelected =
    selectOptions.find((opt) => opt.value === currentValue) || selectOptions[0];

  const renderLocale = ({ label, alpha2 }: any) => (
    <div className="flex items-center gap-1.5">
      <span
        className={`flag-icon flag-icon-${alpha2} h-4 w-5 shrink-0 rounded-sm`}
      />
      <span className="whitespace-nowrap">{label}</span>
    </div>
  );

  return (
    <div className="w-36 shrink-0">
      <KoobInputSelect
        disabled={disabled}
        loading={loading}
        value={optionSelected?.value}
        onValueChange={(newValue) => {
          const selectedOption = selectOptions.find(
            (opt) => opt.value === newValue,
          );
          if (selectedOption) {
            (onChange || updateQueryLocale)?.(selectedOption);
          }
        }}
        options={selectOptions}
        renderValue={renderLocale}
        renderOption={renderLocale}
        data-testid={dataCy}
      />
    </div>
  );
};

export default KoobLocaleSwitcher;
